// Embeddable case-file manager: lists the case's stored files with download links and (for users
// who can manage patient media) a multipart upload form. Upload can't ride the JSON api wrapper, so
// it POSTs FormData with fetch directly; download is a plain same-origin <a download> (the cookie
// travels and the server's Content-Disposition supplies the filename). Rendered inside the case
// detail's Files tab; also refreshes off the media SSE so a teammate's upload shows up live.
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import {
  type PatientMedia,
  type MediaKind,
  type MediaReviewStatus,
  MEDIA_KINDS,
  MEDIA_KIND_LABELS,
  MEDIA_REVIEW_LABELS,
  API_PREFIX,
  formatDateTime,
  formatFileSize,
} from '@alignova/shared';
import { api } from '../../lib/api.ts';
import { useLiveEvent } from '../../lib/useLive.ts';
import { useCan } from '../../auth/useCan.ts';
import { t } from '../../i18n/index.ts';
import { Button, Pill } from '../../ui/primitives.tsx';
import { is3DViewable } from './model3d.ts';

// three.js is ~1 MB — only pull it in when a user actually opens a 3D model.
const CaseModelViewer = lazy(() =>
  import('./CaseModelViewer.tsx').then((m) => ({ default: m.CaseModelViewer })),
);

const inputClass = 'w-full rounded border border-line bg-surface px-3 py-2 text-sm';

function statusTone(status: MediaReviewStatus): 'neutral' | 'good' | 'bad' {
  if (status === 'approved') return 'good';
  if (status === 'rejected') return 'bad';
  return 'neutral';
}

export function CaseFilesPanel({ caseId }: { caseId: string }) {
  const can = useCan();
  const canUpload = can('patient:manage');
  const canVisibility = can('patient:review_media');
  const [items, setItems] = useState<PatientMedia[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<MediaKind | ''>('');
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState<PatientMedia | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = useCallback(() => {
    api
      .get<PatientMedia[]>(`/patients/cases/${caseId}/media`)
      .then(setItems)
      .catch(() => setError(t('cases.files.loadError')));
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  // Another user (or the patient portal) submitted media on some case — cheap to just reload ours.
  useLiveEvent('patient.media.submitted', () => load());
  useLiveEvent('patient.media.reviewed', () => load());

  async function upload(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const file = fileRef.current?.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    if (kind) form.append('kind', kind);
    if (caption.trim()) form.append('caption', caption.trim());
    setUploading(true);
    try {
      const res = await fetch(`${API_PREFIX}/patients/cases/${caseId}/media/upload`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(data.error ?? t('cases.files.uploadError'));
      }
      if (fileRef.current) fileRef.current.value = '';
      setKind('');
      setCaption('');
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('cases.files.uploadError'));
    } finally {
      setUploading(false);
    }
  }

  async function toggleVisible(m: PatientMedia) {
    setError(null);
    try {
      await api.post(`/patients/cases/${caseId}/media/${m.id}/visibility`, {
        clientVisible: !m.clientVisible,
      });
      load();
    } catch {
      setError(t('cases.files.visibilityError'));
    }
  }

  async function removeFile(m: PatientMedia) {
    if (!window.confirm(t('cases.files.deleteConfirm'))) return;
    setError(null);
    try {
      await api.del(`/patients/cases/${caseId}/media/${m.id}`);
      load();
    } catch {
      setError(t('cases.files.deleteError'));
    }
  }

  return (
    <div>
      {error && <p className="mb-3 text-xs text-status-urgent-fg">{error}</p>}

      {items.length === 0 ? (
        <p className="mb-4 rounded border border-line bg-surface-2 px-3 py-6 text-center text-sm text-muted">
          {t('cases.files.empty')}
        </p>
      ) : (
        <div className="mb-4 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-line text-left">
                <th className="px-3 py-2 font-medium">{t('cases.files.col.file')}</th>
                <th className="px-3 py-2 font-medium">{t('cases.files.col.kind')}</th>
                <th className="px-3 py-2 font-medium">{t('cases.files.col.size')}</th>
                <th className="px-3 py-2 font-medium">{t('cases.files.col.status')}</th>
                <th className="px-3 py-2 font-medium">{t('cases.files.col.uploaded')}</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((m) => {
                const hasFile = m.originalName != null;
                const label = m.originalName ?? m.caption ?? m.filePath;
                return (
                  <tr key={m.id} className="border-b border-line align-top">
                    <td className="px-3 py-2">
                      <div className="max-w-[16rem] truncate text-ink" title={label}>
                        {label}
                      </div>
                      {m.caption && m.originalName && (
                        <div className="max-w-[16rem] truncate text-xs text-muted" title={m.caption}>
                          {m.caption}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Pill tone="neutral">{MEDIA_KIND_LABELS[m.kind]}</Pill>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted">
                      {formatFileSize(m.sizeBytes)}
                    </td>
                    <td className="px-3 py-2">
                      <Pill tone={statusTone(m.status)}>{MEDIA_REVIEW_LABELS[m.status]}</Pill>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-muted">
                      {formatDateTime(m.createdAt)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap justify-end gap-2">
                        {canVisibility && (
                          <button
                            type="button"
                            onClick={() => toggleVisible(m)}
                            className={`inline-flex items-center rounded border px-3 py-1 text-xs font-medium ${
                              m.clientVisible
                                ? 'border-primary text-primary'
                                : 'border-line text-muted'
                            } hover:bg-surface-2`}
                            title={m.clientVisible ? t('cases.files.visible') : t('cases.files.hidden')}
                          >
                            {m.clientVisible ? t('cases.files.visible') : t('cases.files.hidden')}
                          </button>
                        )}
                        {hasFile && is3DViewable(m) && (
                          <button
                            type="button"
                            onClick={() => setViewing(m)}
                            className="inline-flex items-center rounded border border-primary px-3 py-1 text-xs font-medium text-primary hover:bg-surface-2"
                          >
                            {t('cases.files.view3d')}
                          </button>
                        )}
                        {hasFile ? (
                          <a
                            className="inline-flex items-center rounded border border-line px-3 py-1 text-xs font-medium text-ink hover:bg-surface-2"
                            href={`${API_PREFIX}/patients/cases/${caseId}/media/${m.id}/download`}
                            download={m.originalName ?? true}
                          >
                            {t('cases.files.download')}
                          </a>
                        ) : (
                          <span className="self-center text-xs text-muted">{t('cases.files.legacyRef')}</span>
                        )}
                        {canUpload && (
                          <button
                            type="button"
                            onClick={() => removeFile(m)}
                            className="inline-flex items-center rounded border border-line px-3 py-1 text-xs font-medium text-status-urgent-fg hover:bg-surface-2"
                          >
                            {t('cases.files.delete')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {canUpload && (
        <form className="space-y-3 border-t border-line pt-4" onSubmit={upload}>
          <h3 className="text-sm font-semibold text-ink">{t('cases.files.uploadHeading')}</h3>
          <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
            <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.files.choose')}</label>
            <input ref={fileRef} type="file" className={inputClass} required />
            <p className="mt-1 text-xs text-muted">{t('cases.files.tooLargeHint')}</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.files.kind')}</label>
              <select
                className={inputClass}
                value={kind}
                onChange={(e) => setKind(e.target.value as MediaKind | '')}
              >
                <option value="">{t('cases.files.kindAuto')}</option>
                {MEDIA_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {MEDIA_KIND_LABELS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:grid md:grid-cols-[11rem_minmax(0,1fr)] md:items-start md:gap-4">
              <label className="mb-1 block text-sm text-muted md:mb-0 md:pt-2">{t('cases.files.caption')}</label>
              <input
                className={inputClass}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={500}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={uploading}>
              {uploading ? t('cases.files.uploading') : t('cases.files.upload')}
            </Button>
          </div>
        </form>
      )}

      {viewing && (
        <Suspense fallback={null}>
          <CaseModelViewer caseId={caseId} media={viewing} onClose={() => setViewing(null)} />
        </Suspense>
      )}
    </div>
  );
}
